import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Row,
  Col,
} from "reactstrap"
import { getAllFirms } from "../../helpers/backendHelpers/firm"
import { getAllPlans } from "../../helpers/backendHelpers/plan"
import { getAllCouponCode } from "../../helpers/backendHelpers/couponCode"
import { updateSubscriptionV2, getSubscriptionInfoV2 } from "../../helpers/backendHelpers/manageSubscription"
import dayjs from "dayjs"
import { SaveToast } from "../../components/Common/SaveToast"

const EditModal = ({ isOpen, toggle, data, onUpdateSuccess }) => {
  const [firms, setFirms] = useState([])
  const [plans, setPlans] = useState([]) // Plan groups
  const [couponList, setCouponList] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [selectedFirmId, setSelectedFirmId] = useState("")
  const [selectedPlanId, setSelectedPlanId] = useState("") // specific plan category ID
  const [selectedPlanGroup, setSelectedPlanGroup] = useState(null)
  const [categories, setCategories] = useState([])
  const [discountAmount, setDiscountAmount] = useState(0)
  const [couponCode, setCouponCode] = useState("")
  const [paymentMode, setPaymentMode] = useState("")
  const [gstNo, setGstNo] = useState("")
  const [activeFirmIds, setActiveFirmIds] = useState([])
  const [priceDetails, setPriceDetails] = useState(null)
  const [priceLoading, setPriceLoading] = useState(false)
  const [priceError, setPriceError] = useState("")

  // Re-fetch whenever the plan OR the subscription being edited changes. Keying on the
  // plan alone left stale totals when another subscription with the same plan was opened.
  // `cancelled` drops responses that arrive after the plan/subscription changed.
  useEffect(() => {
    setPriceDetails(null)
    setPriceError("")
    if (!isOpen || !selectedPlanId || !data?._id) {
      setPriceLoading(false)
      return undefined
    }

    let cancelled = false
    const fetchPriceDetails = async () => {
      setPriceLoading(true)
      try {
        const userId = data?.userId?._id || data?.userId || ""
        const response = await getSubscriptionInfoV2(selectedPlanId, { userId, editingSubscriptionId: data._id })
        if (cancelled) return
        if (response && response.success) {
          setPriceDetails(response.payload)
        } else {
          setPriceError(response?.message || "Failed to fetch price calculations")
        }
      } catch (err) {
        if (!cancelled) setPriceError(err.message || "Failed to fetch price calculations")
      } finally {
        if (!cancelled) setPriceLoading(false)
      }
    }
    fetchPriceDetails()

    return () => {
      cancelled = true
    }
  }, [isOpen, selectedPlanId, data?._id])

  useEffect(() => {
    if (isOpen && data) {
      const payment = data.paymentId || {}
      setSelectedPlanId(data.planId?._id || data.planId || "")
      setSelectedFirmId(payment.invoiceFirmId || (data.activeFirmIds && data.activeFirmIds[0]) || "")
      setDiscountAmount(payment.discountAmount || 0)
      setCouponCode(payment.couponCode || "")
      setPaymentMode(payment.paymentMode || "")
      setActiveFirmIds((data.activeFirmIds || []).map(item => item._id || item))

      fetchFirmsAndPlans()
    }
  }, [isOpen, data])

  const convertToDays = (noOfBillingCycle, billingCycle) => {
    const cycle = (billingCycle || "").toLowerCase()
    if (cycle.includes("day")) return noOfBillingCycle
    if (cycle.includes("month")) return noOfBillingCycle * 30
    if (cycle.includes("year")) return noOfBillingCycle * 365
    return noOfBillingCycle
  }

  const isCategoryValid = (category) => {
    const predecessorSub = data?.previousSubscriptionId
    if (!predecessorSub) return true

    const predecessorPlan = predecessorSub.planId
    if (!predecessorPlan) return true

    const targetGrade = category.grade
    const predecessorGrade = predecessorPlan.grade

    if (targetGrade === predecessorGrade) {
      return true
    }

    // Upgrade check
    if (targetGrade > predecessorGrade) {
      const pendingDays = Math.max(0, dayjs(predecessorSub.expiryDate).diff(dayjs(data.createdAt), "day"))
      const totalPlanDays = convertToDays(predecessorPlan.noOfBillingCycle, predecessorPlan.billingCycle)
      const currentPlanAmount = Number(predecessorPlan.planAmount || 0)
      const perDayPrice = Number((currentPlanAmount / totalPlanDays).toFixed(2))
      const pendingAmount = Number((pendingDays * perDayPrice).toFixed(2))

      return category.planAmount >= pendingAmount
    }

    // Downgrade check
    if (targetGrade < predecessorGrade) {
      if (Number(category.planAmount || 0) <= Number(predecessorPlan.planAmount || 0)) {
        return false
      }
      const pendingDays = Math.max(0, dayjs(predecessorSub.expiryDate).diff(dayjs(data.createdAt), "day"))
      const totalPlanDays = convertToDays(predecessorPlan.noOfBillingCycle, predecessorPlan.billingCycle)
      const currentPlanAmount = Number(predecessorPlan.planAmount || 0)
      const perDayPrice = Number((currentPlanAmount / totalPlanDays).toFixed(2))
      const pendingAmount = Number((pendingDays * perDayPrice).toFixed(2))

      return category.planAmount >= pendingAmount
    }

    return true
  }

  const getPlanAmountById = (planId, categoryList = categories) => {
    const matchedCategory =
      (categoryList || []).find(cat => String(cat._id) === String(planId)) ||
      plans.flatMap(group => group.planCategories || []).find(cat => String(cat._id) === String(planId))

    return Number(
      matchedCategory?.planAmount ??
      data?.paymentId?.planAmount ??
      data?.planId?.planAmount ??
      0
    )
  }

  const calculateCouponDiscountAmount = (coupon, planAmount) => {
    if (!coupon || !planAmount) return 0

    const rawDiscount = coupon.discountType === "percentage"
      ? (Number(planAmount) * Number(coupon.discountValue || 0)) / 100
      : Number(coupon.discountValue || 0)

    return Number(Math.min(Number(planAmount), rawDiscount).toFixed(2))
  }

  const applyCouponDiscount = (code, planId, categoryList = categories) => {
    const trimmedCode = (code || "").trim().toUpperCase()
    const existingCouponCode = (data?.paymentId?.couponCode || "").toString().trim().toUpperCase()
    const existingDiscountAmount = Number(data?.paymentId?.discountAmount || 0)
    const currentUserId = (data?.userId?._id || data?.userId || "").toString()

    if (!trimmedCode) {
      setDiscountAmount(0)
      return
    }

    // Preserve the saved discount for the original coupon attached to this payment,
    // even if that coupon is now expired/inactive and no longer appears in the active list.
    if (existingCouponCode && trimmedCode === existingCouponCode) {
      setDiscountAmount(existingDiscountAmount)
      return
    }

    const matchedCoupon = couponList.find(coupon =>
      coupon?.couponCode?.toString().trim().toUpperCase() === trimmedCode &&
      (
        !Array.isArray(coupon?.userIds) ||
        coupon.userIds.length === 0 ||
        coupon.userIds.some(id => (id?._id || id)?.toString() === currentUserId)
      ) &&
      coupon?.isActive !== false &&
      coupon?.isExpired !== true
    )

    if (!matchedCoupon) {
      setDiscountAmount(0)
      return
    }

    const planAmount = getPlanAmountById(planId, categoryList)
    setDiscountAmount(calculateCouponDiscountAmount(matchedCoupon, planAmount))
  }

  const fetchFirmsAndPlans = async () => {
    setLoading(true)
    try {
      const [plansRes, couponRes, firmsRes] = await Promise.all([
        getAllPlans(),
        getAllCouponCode(),
        data?.userId?._id ? getAllFirms(data.userId._id) : Promise.resolve(null)
      ])

      if (firmsRes && data?.userId?._id) {
        const firmsList = firmsRes?.payload?.data || []
        setFirms(firmsList)
        const currentFirmId = data.paymentId?.invoiceFirmId || (data.activeFirmIds && data.activeFirmIds[0]) || ""
        const matchedFirm = firmsList.find(f => f._id === currentFirmId)
        setGstNo(matchedFirm?.GSTNo || "")
      }

      const plansList = plansRes?.payload?.data || []
      setCouponList(couponRes?.payload?.data || [])

      // Filter to keep active plan groups
      let activeGroups = plansList.filter(g => g.isActive).map(group => {
        const grade = group.grade;
        const categoriesWithGrade = (group.planCategories || []).map(cat => ({
          ...cat,
          grade: grade
        }));
        return {
          ...group,
          planCategories: categoriesWithGrade
        };
      });

      const currentPlan = data?.planId
      const currentPlanId = currentPlan?._id || currentPlan || ""

      // Find if any group has the current plan category
      let matchedGroup = activeGroups.find(group =>
        group.planCategories && group.planCategories.some(cat => cat._id === currentPlanId)
      )

      if (!matchedGroup && currentPlanId) {
        // Look in all plans including inactive ones
        let foundGroup = plansList.find(group =>
          group.planCategories && group.planCategories.some(cat => cat._id === currentPlanId)
        )
        if (foundGroup) {
          const grade = foundGroup.grade;
          const categoriesWithGrade = (foundGroup.planCategories || []).map(cat => ({
            ...cat,
            grade: grade
          }));
          matchedGroup = {
            ...foundGroup,
            planCategories: categoriesWithGrade
          };
        }
      }

      if (!matchedGroup && currentPlanId) {
        // Fallback for custom or direct plan entries
        const name = currentPlan?.name || data.planId?.name || "Current Plan"
        const grade = currentPlan?.grade || data.planId?.grade || 1
        matchedGroup = {
          name: name,
          grade: grade,
          planCategories: [{
            _id: currentPlanId,
            planAmount: data.paymentId?.planAmount || currentPlan?.planAmount || 0,
            noOfAllowedGST: currentPlan?.noOfAllowedGST || 1,
            noOfBillingCycle: currentPlan?.noOfBillingCycle || 1,
            billingCycle: currentPlan?.billingCycle || "Month",
            noOfAllowedUsers: currentPlan?.noOfAllowedUsers || 1,
            grade: grade
          }]
        }
        activeGroups.unshift(matchedGroup)
      }

      setPlans(activeGroups)

      if (matchedGroup) {
        setSelectedPlanGroup(matchedGroup)
        setCategories(matchedGroup.planCategories || [])
      }
    } catch (error) {
      SaveToast({ message: "Failed to load firms or plans", type: "error" })
    } finally {
      setLoading(false)
    }
  }

  const handleFirmChange = (e) => {
    const firmId = e.target.value
    setSelectedFirmId(firmId)
    const selectedFirm = firms.find(f => f._id === firmId)
    setGstNo(selectedFirm?.GSTNo || "")
  }

  const handlePlanGroupChange = (e) => {
    const groupName = e.target.value
    const group = plans.find(p => p.name === groupName)
    setSelectedPlanGroup(group || null)
    const newCategories = group?.planCategories || []
    setCategories(newCategories)

    if (newCategories.length > 0) {
      const nextPlanId = newCategories[0]._id
      setSelectedPlanId(nextPlanId)
      if ((couponCode || "").trim()) {
        applyCouponDiscount(couponCode, nextPlanId, newCategories)
      }
    } else {
      setSelectedPlanId("")
    }
  }

  const handleCategoryChange = (e) => {
    const nextPlanId = e.target.value
    setSelectedPlanId(nextPlanId)
    if ((couponCode || "").trim()) {
      applyCouponDiscount(couponCode, nextPlanId)
    }
  }

  const handleCouponCodeChange = (e) => {
    const nextCouponCode = e.target.value.toUpperCase()
    setCouponCode(nextCouponCode)
    applyCouponDiscount(nextCouponCode, selectedPlanId)
  }

  const normalizeDiscountAmount = (value) => {
    const parsedValue = Number(value)
    if (!Number.isFinite(parsedValue) || parsedValue < 0) {
      return "0.00"
    }

    return parsedValue.toFixed(2)
  }

  const handleDiscountAmountBlur = () => {
    setDiscountAmount(normalizeDiscountAmount(discountAmount))
  }

  const activeCategory = categories.find(cat => cat._id === selectedPlanId)
  const allowedGST = activeCategory?.noOfAllowedGST || data?.planId?.noOfAllowedGST || 1

  const isActiveFirmSelectionRequired = activeCategory && allowedGST < firms.length
  const isFirmSelectionValid = !isActiveFirmSelectionRequired || (activeFirmIds.length === allowedGST)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (isActiveFirmSelectionRequired && !isFirmSelectionValid) {
      window.alert(`Please select exactly ${allowedGST} active firm(s).`)
      return
    }
    setSubmitting(true)
    try {
      const payload = {
        planId: selectedPlanId,
        invoiceFirmId: selectedFirmId,
        discountAmount: Number(normalizeDiscountAmount(discountAmount)) || 0,
        couponCode: couponCode || "",
        paymentMode: paymentMode,
        GSTNo: gstNo || "",
      }
      if (isActiveFirmSelectionRequired) {
        payload.activeFirmIds = activeFirmIds
      }

      const res = await updateSubscriptionV2(data._id, payload)
      if (res && res.success) {
        toggle()
        if (onUpdateSuccess) {
          onUpdateSuccess()
        }
      }
    } catch (error) {
      console.error("Error updating subscription:", error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle} className="border-bottom">
        <span className="font-size-18 text-primary font-weight-bold">Edit Active Subscription & Invoice</span>
      </ModalHeader>
      <ModalBody className="p-4">
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Fetching firm and plan details...</p>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label for="selectFirm" className="font-weight-semibold">Select Firm To Create Invoice</Label>
                  <Input
                    type="select"
                    id="selectFirm"
                    value={selectedFirmId}
                    onChange={handleFirmChange}
                    required
                  >
                    <option value="">-- Select Firm --</option>
                    {firms.map(firm => (
                      <option key={firm._id} value={firm._id}>
                        {firm.firmName}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label for="gstNumber" className="font-weight-semibold">Selected Firm GST Number</Label>
                  <Input
                    type="text"
                    id="gstNumber"
                    value={gstNo}
                    onChange={(e) => setGstNo(e.target.value)}
                    placeholder="Enter GST Number"
                  />
                </FormGroup>
              </Col>
            </Row>

            {isActiveFirmSelectionRequired && (
              <Row>
                <Col md={12}>
                  <FormGroup className="mb-3">
                    <Label className="form-label text-danger font-weight-bold">
                      Downgrade Detected: Select Active Firms (Max: {allowedGST})
                    </Label>
                    <div className="border p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto', background: '#fff' }}>
                      {firms.length === 0 ? (
                        <div className="text-muted">No firms found for this user.</div>
                      ) : (
                        firms.map(f => {
                          const isChecked = activeFirmIds.some(id => String(id) === String(f._id));
                          const limitReached = !isChecked && activeFirmIds.length >= allowedGST;

                          return (
                            <div
                              key={f._id}
                              className="d-flex align-items-center mb-2"
                              style={{ cursor: limitReached ? 'default' : 'pointer' }}
                              onClick={() => {
                                if (limitReached && !isChecked) return;

                                let currentIds = [...activeFirmIds];
                                if (isChecked) {
                                  currentIds = currentIds.filter(id => String(id) !== String(f._id));
                                } else {
                                  currentIds.push(f._id);
                                }
                                setActiveFirmIds(currentIds);
                              }}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input me-2"
                                style={{ cursor: 'pointer' }}
                                checked={isChecked}
                                readOnly
                                disabled={limitReached}
                              />
                              <span className={`${limitReached ? 'text-muted' : 'text-dark'}`} style={{ userSelect: 'none' }}>
                                {f.firmName} ({f.GSTNo || 'No GST'})
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                    {allowedGST > 0 && (
                      <div className={`small mt-1 ${isFirmSelectionValid ? 'text-primary' : 'text-danger fw-bold'}`}>
                        Selected: {activeFirmIds.length} / {allowedGST}
                        {!isFirmSelectionValid && " (Please select exactly this many firms)"}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label for="selectPlan" className="font-weight-semibold">Select Plan</Label>
                  <Input
                    type="select"
                    id="selectPlan"
                    value={selectedPlanGroup?.name || ""}
                    onChange={handlePlanGroupChange}
                    required
                  >
                    <option value="">-- Select Plan --</option>
                    {plans.map(group => (
                      <option key={group.name} value={group.name}>
                        {group.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label for="selectCategory" className="font-weight-semibold">Select Category</Label>
                  <Input
                    type="select"
                    id="selectCategory"
                    value={selectedPlanId}
                    onChange={handleCategoryChange}
                    required
                    disabled={!selectedPlanGroup}
                  >
                    <option value="">-- Select Category --</option>
                    {categories.filter(isCategoryValid).map(category => (
                      <option key={category._id} value={category._id}>
                        {category.planAmount} ({category.noOfAllowedGST} {category.noOfAllowedGST === 1 ? "Firm" : "Firms"}) - {category.noOfBillingCycle} {category.billingCycle}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            {activeCategory && (
              <div className="p-3 rounded mb-4" style={{ background: "linear-gradient(135deg, #f0f4f8 0%, #e2ebf0 100%)", border: "1px solid rgba(0,0,0,0.05)" }}>
                <Row className="align-items-center">
                  <Col sm={6}>
                    <div className="text-muted font-size-12 text-uppercase font-weight-bold mb-1">Pricing & Duration</div>
                    <div className="font-size-22 font-weight-bold text-primary" style={{ letterSpacing: "-0.5px" }}>
                      ₹{activeCategory.planAmount} <span className="font-size-13 font-weight-normal text-muted">/ {activeCategory.noOfBillingCycle} {activeCategory.billingCycle}</span>
                    </div>
                  </Col>
                  <Col sm={6} className="text-sm-end mt-2 mt-sm-0">
                    <div className="font-size-13 text-dark mb-1">
                      <i className="bx bx-buildings mr-1 text-primary"></i> <strong>Firms Limit:</strong> {activeCategory.noOfAllowedGST} {activeCategory.noOfAllowedGST === 1 ? "Firm" : "Firms"}
                    </div>
                    <div className="font-size-13 text-dark">
                      <i className="bx bx-user mr-1 text-primary"></i> <strong>Users Limit:</strong> {activeCategory.noOfAllowedUsers} {activeCategory.noOfAllowedUsers === 1 ? "User" : "Users"}
                    </div>
                  </Col>
                </Row>
              </div>
            )}

            {priceLoading ? (
              <div className="text-center py-3">
                <Spinner color="primary" size="sm" />
                <p className="mt-1 font-size-12 text-muted">Calculating pricing details...</p>
              </div>
            ) : priceError ? (
              <div className="alert alert-danger font-size-13 py-2 px-3 mb-4">{priceError}</div>
            ) : priceDetails && (
              <div className="p-3 rounded mb-4" style={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}>
                <div className="text-muted font-size-12 text-uppercase font-weight-bold mb-3 border-bottom pb-2">
                  <i className="bx bx-receipt mr-1 text-primary"></i> Order Summary
                </div>
                {(() => {
                  const basePrice = priceDetails.planAmount || 0;
                  const rawPending = priceDetails.pendingAmount || 0;
                  const discount = Number(discountAmount) || 0;
                  const taxableAmount = Math.max(0, Number((basePrice - discount).toFixed(2)));
                  const gst = Number((taxableAmount * 0.18).toFixed(2));
                  const grandTotal = Number((taxableAmount + gst).toFixed(2));
                  const formattedExpiry = priceDetails.expiryDate ? dayjs(priceDetails.expiryDate).format("DD-MM-YYYY") : "-";

                  return (
                    <div>
                      <Row className="mb-2 font-size-13">
                        <Col xs={6} className="text-muted">Base Plan Price:</Col>
                        <Col xs={6} className="text-end font-weight-semibold">₹{priceDetails.basePlanAmount?.toFixed(2)}</Col>
                      </Row>
                      {rawPending > 0 && !priceDetails.advanceSubscription && (
                        <Row className="mb-2 font-size-13 text-success">
                          <Col xs={6}>Balance Adjustment (Predecessor Credit):</Col>
                          <Col xs={6} className="text-end font-weight-semibold">- ₹{rawPending.toFixed(2)}</Col>
                        </Row>
                      )}
                      <Row className="mb-2 font-size-13 border-top pt-2">
                        <Col xs={6} className="text-muted">Plan Total (Taxable Amount):</Col>
                        <Col xs={6} className="text-end font-weight-semibold">₹{basePrice.toFixed(2)}</Col>
                      </Row>
                      {discount > 0 && (
                        <Row className="mb-2 font-size-13 text-danger">
                          <Col xs={6}>Discount Applied:</Col>
                          <Col xs={6} className="text-end font-weight-semibold">- ₹{discount.toFixed(2)}</Col>
                        </Row>
                      )}
                      <Row className="mb-2 font-size-13">
                        <Col xs={6} className="text-muted">GST (18%):</Col>
                        <Col xs={6} className="text-end font-weight-semibold">₹{gst.toFixed(2)}</Col>
                      </Row>
                      <Row className="mb-2 font-size-15 border-top pt-2 font-weight-bold text-primary">
                        <Col xs={6}>Grand Total:</Col>
                        <Col xs={6} className="text-end">₹{grandTotal.toFixed(2)}</Col>
                      </Row>
                      <Row className="mb-0 font-size-13 border-top pt-2 text-muted">
                        <Col xs={6}>New Expiry Date:</Col>
                        <Col xs={6} className="text-end font-weight-bold text-dark">{formattedExpiry}</Col>
                      </Row>
                    </div>
                  );
                })()}
              </div>
            )}

            <Row>
              <Col md={12}>
                <FormGroup className="mb-3">
                  <Label for="paymentMode" className="font-weight-semibold">Payment Mode</Label>
                  <Input
                    type="select"
                    id="paymentMode"
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    required
                  >
                    <option value="">-- Select Payment Mode --</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="online">Online</option>
                    <option value="razorpay">Razorpay</option>
                    <option value="other">Other</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label for="discountAmount" className="font-weight-semibold">Discount Amount (₹)</Label>
                  <Input
                    type="number"
                    id="discountAmount"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                    onBlur={handleDiscountAmountBlur}
                    min={0}
                    step="0.01"
                    placeholder="Enter discount amount"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label for="couponCode" className="font-weight-semibold">Coupon Code</Label>
                  <Input
                    type="text"
                    id="couponCode"
                    value={couponCode}
                    onChange={handleCouponCodeChange}
                    placeholder="Enter coupon code (optional)"
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
              <Button type="button" color="light" onClick={toggle} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={submitting || !isFirmSelectionValid}>
                {submitting ? (
                  <>
                    <Spinner size="sm" className="mr-1" /> Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </Form>
        )}
      </ModalBody>
    </Modal>
  )
}

EditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  data: PropTypes.object,
  onUpdateSuccess: PropTypes.func,
}

export default EditModal
